/*
Author: Alexey Usov (dax@xdax.ru, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file
*/
import {useLocalStorage} from '@vueuse/core';
import {type InjectionKey, provide, reactive, inject, watch, watchEffect, unref} from 'vue';
import {MOBILE_MEDIA_QUERY} from '@/composables/mobile';
import type {GameData} from './data';
import type {GroupMode, PublicFilter} from './filter';
import type {BlueprintModel} from './model/store';
import {BlueprintItemState, DEFAULT_BLUEPRINT_SPLIT, type BlueprintItemStateValues} from './types';

type BlueprintItemStateColorClass = Record<BlueprintItemStateValues, string>;

const SavedKeys = [
    'darkTheme',
    'colorfulLinks',
    'tier',
    'tierEqual',
    'groupTier',
    'autoSolveGraph',
    'solvePrecision',
    'scale',
    'colorfulLinks',
    'dragAndDropEnabled',
    'dragAndScrollEnabled',
    'dragAndScrollOutsideWindow',
    'overflowScrollEnabled',
    'pointAndClickEnabled',
    'scrollScaleEnabled',
    'blueprintCompress',
    'blueprintEncode',
    'blueprintSplit',
    'showSummary',
    'showSummaryCompact',
    'showCountControlsOnWindow',
    'appendFileNameToLink',
    'leftPanelDisplayMode',
    //fork addition: full left-panel group mode. Listed after 'groupTier'
    //on purpose: load() applies keys in stored order, so the precise
    //groupMode wins over the legacy boolean
    'groupMode',
] as const;
type SavedKey = typeof SavedKeys[number];
type SavedObject = {
    [k in SavedKey]: unknown;
};

function filterSavedKeys(object: SavedObject) {
    const savedObject = SavedKeys.reduce((obj, key) => {
        obj[key] = object[key];
        return obj;
    }, {} as SavedObject);
    return savedObject;
}

function isTouchDevice() {
    try {
        return (('ontouchstart' in window) ||
            (navigator.maxTouchPoints > 0));
    } catch(err) {
        return false;
    }
}

//fork addition: hybrid laptops report maxTouchPoints > 0 and get misread
//as touch-only, which disables mouse drag/pan. A fine primary pointer
//(mouse/trackpad) means desktop interactions must stay enabled.
export function hasFinePointer() {
    try {
        return !!window.matchMedia?.('(pointer: fine)').matches;
    } catch(err) {
        return false;
    }
}

class Settings implements SavedObject {
    private _filter: PublicFilter;
    private _blueprintModel: BlueprintModel;
    private _isTouchDevice;

    constructor(blueprintModel: BlueprintModel, filter: PublicFilter) {
        this._blueprintModel = blueprintModel;
        this._filter = filter;

        const touchDevice = isTouchDevice();
        const finePointer = hasFinePointer();
        this.dragAndDropEnabled = finePointer || !touchDevice;
        this.dragAndScrollEnabled = finePointer || !touchDevice;
        this.overflowScrollEnabled = !touchDevice;
        this.pointAndClickEnabled = touchDevice;
        //fork change: wheel scrolls by default (normal web behaviour);
        //wheel-zoom stays available as the "Enable Scale On Wheel" setting
        this.scrollScaleEnabled = false;
        this._isTouchDevice = touchDevice;
    }

    get isTouchDevice() { return this._isTouchDevice; }

    iconSize = 32;
    itemStateColor: BlueprintItemStateColorClass = {
        [BlueprintItemState.None]: 'bg-window-idle',
        [BlueprintItemState.CannotLinkTarget]: 'bg-error',
        [BlueprintItemState.CanLinkTarget]: 'bg-success',
        [BlueprintItemState.LinkAlreadyExists]: 'bg-warning',
        [BlueprintItemState.CanLinkWithRecipeChange]: 'bg-info',
    };
    scale = 1;
    darkTheme = false;
    colorfulLinks = true;
    dragAndDropEnabled;
    dragAndScrollEnabled;
    dragAndScrollOutsideWindow = true;
    overflowScrollEnabled;
    pointAndClickEnabled;
    scrollScaleEnabled;

    blueprintCompress = true;
    blueprintEncode = true;
    blueprintSplit = DEFAULT_BLUEPRINT_SPLIT;

    showSummary = true;
    showSummaryCompact = true;
    showCountControlsOnWindow = true;

    appendFileNameToLink = true;

    //left toolbox list entries: 'icons' | 'both' (icons + names) | 'names'
    leftPanelDisplayMode = 'both';

    get tier() { return this._filter.tier; }
    set tier(tier: number | undefined) { this._filter.tier = tier; }
    get tierEqual() { return this._filter.tierEqual; }
    set tierEqual(tierEqual: number) { this._filter.tierEqual = tierEqual; }
    get groupTier() { return this._filter.groupTier; }
    set groupTier(groupTier: boolean) { this._filter.groupTier = groupTier; }
    get groupMode() { return this._filter.groupMode; }
    set groupMode(groupMode: GroupMode) { this._filter.groupMode = groupMode; }
    get autoSolveGraph() { return this._blueprintModel.autoSolveGraph; }
    set autoSolveGraph(autoSolveGraph: boolean) { this._blueprintModel.autoSolveGraph = autoSolveGraph; }
    get solvePrecision() { return this._blueprintModel.solvePrecision; }
    set solvePrecision(solvePrecision: number) { this._blueprintModel.solvePrecision = solvePrecision; }

    save() {
        return filterSavedKeys(this);
    }
    load(settings: SavedObject) {
        const self = this as SavedObject;
        for(const [key, value] of Object.entries(settings)) {
            const _key = key as SavedKey;
            //prevent unnecessary change events, if corresponding option is not changed
            if((value !== undefined) && (self[_key] !== value)) {
                self[_key] = value;
            }
        }
    }
}

export const SettingsKey = Symbol('Settings') as InjectionKey<Settings>;
export const provideSettings = (gameData: GameData, blueprintModel: BlueprintModel, filter: PublicFilter) => {
    const settings = reactive(new Settings(blueprintModel, filter));
    const name = gameData.gameDescription.name;
    provide(SettingsKey, settings);
    //fork change: only an explicit 'groupMode' key in the raw stored
    //settings (written by the current UI) is honoured. Profiles saved
    //before it existed carry just the legacy 'groupTier' boolean,
    //which was the old forced default rather than a choice the user
    //made — without an explicit groupMode it is dropped from the
    //loaded values, so the game default applies instead (CoI
    //categories for CoI). Read the raw value before useLocalStorage
    //merges defaults in, because the merged result always contains a
    //groupMode and could not be told apart from an explicit one
    let hasExplicitGroupMode = false;
    try {
        const raw = window.localStorage.getItem(`settings-${name}`);
        const parsed = raw ? JSON.parse(raw) : undefined;
        hasExplicitGroupMode = !!(parsed && ('groupMode' in parsed));
    } catch(err) {
        //unreadable storage — defaults apply
    }
    const savedSettings = useLocalStorage<SavedObject>(`settings-${name}`, settings.save(), {
        mergeDefaults: true,
    });
    const storedValues = unref(savedSettings);
    if(!hasExplicitGroupMode && storedValues)
        delete (storedValues as Record<string, unknown>).groupTier;
    settings.load(storedValues);
    //fork addition: a persisted false from the touch misdetection must not
    //keep mouse pan/drag disabled on devices with a mouse or trackpad
    if(hasFinePointer()) {
        settings.dragAndScrollEnabled = true;
        settings.dragAndDropEnabled = true;
    }
    //fork addition (Stage I): small-screen session defaults — mobile
    //is tap-first, desktop defaults are never touched. On a viewport
    //at most 900px wide: (a) point-and-click is enabled, the upstream
    //touch mode — tap a factory to arm placement, tap an output port
    //then a target input to link — because drag-linking with a finger
    //is fiddly; (b) a persisted desktop scale above 1 is clamped to
    //1, so the fixed 300px cards stay readable at phone width (the
    //board pans; zooming back up stays one slider away). Both values
    //remain ordinary settings and persist through the normal save
    //below, exactly like any other toggle flip.
    try {
        if(window.matchMedia?.(MOBILE_MEDIA_QUERY).matches) {
            settings.pointAndClickEnabled = true;
            if(settings.scale > 1)
                settings.scale = 1;
        }
    } catch(err) {
        //no matchMedia — desktop defaults apply
    }
    watchEffect(() => Object.assign(savedSettings.value, settings.save()));
    watch(savedSettings, () => settings.load(unref(savedSettings)));
    return settings;
};
export const injectSettings = () => {
    const settings = inject(SettingsKey);
    if(!settings)
        throw new Error('settings not instantiated');
    return settings;
};
